import { createSelector } from 'reselect';

import { emptyArray } from 'Lib/constants/placeholder';
import { createKeySelector } from 'Lib/reduxUtils';

import type { ModalQueue, ModalState } from './types';

/**
 * This maintains a mapping of modal IDs to their props, which can contain callbacks that cannot be
 * serialized and saved in the Redux store.
 *
 * This precludes saving modal state, but we don't currently try to do that so YOLO.
 */
const modalPropsById = new Map();
export const getModalProps = (id: string) => modalPropsById.get(id);
export const setModalProps = (id: string, props: any) => {
  modalPropsById.set(id, props);
};
export const unsetModalProps = (id: string) => {
  modalPropsById.delete(id);
};

/**
 * Selects modals slice of the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {ModalStateV} list of modals
 */
export const selectModals = createKeySelector('modals');

/**
 * Selects the queue from modals
 *
 * @param  {ModalState} modals - modals state
 * @return {Array} list of modals
 */
export const selectQueueFromModals = (modals: ModalState) => modals.queue || emptyArray;

/**
 * Selects the current modal from the queue
 *
 * @param  {Array} queue - modal queue state
 * @return {Object} data for the current modal
 */
export const selectCurrentModalFromQueue = (queue: ModalQueue) => queue[0];

/**
 * Selects the last modal from the queue
 *
 * @param  {Array} queue - modal queue state
 * @return {Object} data for the last queued modal
 */
export const selectLastModalFromQueue = (queue: ModalQueue) => queue[queue.length - 1];

/**
 * Gets modalProps for the supplied modal
 *
 * @param  {Object} modal - modal
 * @return {Object} props for the current modal
 */
export const selectModalPropsForModal = (modal: any) => modal && getModalProps(modal.id);

/**
 * Selects modals queue from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<Object>} list of modals
 */
export const selectModalsQueue = createSelector(selectModals, selectQueueFromModals);

/**
 * Selects the current modal from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Object} data for the current modal
 */
export const selectCurrentModal = createSelector(selectModalsQueue, selectCurrentModalFromQueue);

/**
 * Selects the current modal from the state and returns its props
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Object} data for the current modal including its props
 */
export const selectCurrentModalProps = createSelector(selectCurrentModal, selectModalPropsForModal);

/**
 * Returns whether there is currently a modal to display
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether or not there is a modal in the queue
 */
export const selectHasCurrentModal = createSelector(selectModalsQueue, (queue) => queue.length > 0);
